/**
 * P6 공표통계 대조 페이지 (DESIGN §8.5 · RUNBOOK P7-3).
 *
 * 이 페이지가 답하는 질문은 "가동률이 몇 %인가"가 아니라 **"그 숫자가 정의를 얼마나
 * 타는가"** 다. 다른 페이지는 정의 하나를 골라 놓고 값을 보여주지만, 여기서는 그
 * 고르기 자체를 화면에 펼친다 — 재현 리포트가 하는 일의 화면판이다.
 *
 * 지켜야 할 세 가지:
 *
 * 1. **두 지표를 같은 수로 쓰지 않는다** (§3.5). 시간 가중 가동률은 기간 지표,
 *    스냅샷 고장률은 한 시각의 개체 비율이다. 나란히 두되 그 문장을 접지 않고
 *    페이지 머리에 적는다 — 접어 두면 둘을 더해 보거나 서로의 검증에 쓴다.
 * 2. **값을 바꾸지 않는 축도 결과다.** GAP_SUSPECT 축이 죽어 보이는 것은 버그가
 *    아니라 "수집이 깨끗하더라"는 관측이다.
 * 3. **없는 것은 없다고 말한다.** 격자가 비면 빈 히트맵 대신 채우는 명령을,
 *    패널 회귀가 식별되지 않으면 계수 대신 사유를 보여준다.
 */

import { Section } from '@/components/Section'
import { PanelResult } from './reconcile/PanelResult'
import { SensitivityGrid } from './reconcile/SensitivityGrid'
import './ReconcilePage.css'

export function ReconcilePage() {
  return (
    <div className="reconcile">
      {/* 페이지 제목은 sub-nav 에 있다 — h1 을 두 번 두지 않는다 */}
      <header className="reconcile__intro">
        <p className="reconcile__lead">
          같은 관측 데이터라도 <strong>무엇을 고장으로 셀지</strong>, <strong>얼마나 오래된
          상태를 현재로 믿을지</strong>, <strong>수집 공백과 겹친 시간을 쓸지</strong>에 따라
          전국 숫자가 달라집니다. 이 페이지는 그 선택을 숨기지 않고 16개 조합을 모두 펼칩니다.
        </p>

        {/* 요구 1 — 이 문장은 접지 않는다. 접으면 두 수를 같은 것으로 쓴다 */}
        <p className="reconcile__notsame" data-testid="metrics-differ">
          <strong>시간 가중 가동률과 스냅샷 고장률은 원리상 다른 수입니다 (§3.5).</strong>{' '}
          가동률 A = ΣT_up / ΣT_obs 는 <em>기간</em> 지표로 관측된 시간을 분모에 두고, 스냅샷
          고장률 R_snap = |DOWN| / |UP∪DOWN| 은 <em>한 시각</em>의 개체 수 비율입니다. 오래
          고장난 1기와 잠깐 고장난 1기가 후자에서는 같은 무게를 갖습니다. 그래서{' '}
          <strong>1 − A 와 R_snap 을 같은 수로 쓰거나 서로의 검증에 쓰면 안 됩니다.</strong>{' '}
          아래 격자는 둘을 나란히 두지만 같은 색 눈금에 태우지 않습니다.
        </p>
      </header>

      <Section
        index={1}
        title="정의를 바꾸면 숫자가 얼마나 움직이나"
        lead="정의 2 × STALE 임계 4 × 수집공백 처리 2 = 16셀. 값이 크게 움직이는 축과 전혀 안 움직이는 축이 같이 보여야 어느 선택이 결론을 만드는지 알 수 있다."
        layout="full"
      >
        <SensitivityGrid />
      </Section>

      <Section
        index={2}
        title="날씨가 고장을 설명하는가"
        lead="지금은 답할 수 없다. 계수를 지어내는 대신 왜 못 하는지를 보여준다 — '효과가 없다'와 '물어볼 수 없다'는 다른 진술이다."
        layout="full"
      >
        <PanelResult />
      </Section>
    </div>
  )
}
